import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from './ui/dialog';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Download, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  Calendar,
  CreditCard,
  User,
  Mail,
  Phone,
  DollarSign,
  Bus,
  Wallet,
  Utensils, 
  GraduationCap,
  Eye, 
  XCircle,
  X,
  Calculator, 
  Network
} from 'lucide-react';
import { api } from '../services/api';
import PaymentHistoryModal from './PaymentHistoryModal';
import ModalModifierReductionEleve from './ModalModifierReductionEleve';
import ModalModifierFraisDebutAnnee from './ModalModifierFraisDebutAnnee';
import JournalModificationsEleve from './JournalModificationsEleve';
import LoadingSpinner from './LoadingSpinner';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import ToastPortal from './ToastPortal';
import { useFeatures } from '../contexts/FeaturesContext';

// `ouvrirAuChargement` ('reduction' | 'frais') : fenêtre de correction demandée
// par un lien direct (panneau des alertes critiques), ouverte une fois le
// dossier affiché, comme si l'agent avait cliqué le bouton lui-même.
// `onOuvertureConsommee` prévient l'appelant qu'elle a été traitée.
const ComptaPaymentDetailsModal = ({ isOpen, onClose, eleve, anneeActive, formatMontant, onAddPayment, modesPaiement, ouvrirAuChargement = null, onOuvertureConsommee }) => {
  // « Soldé » atteste qu'un mois a été payé au site d'ou l'eleve vient : le
  // statut n'existe qu'apres un transfert inter-sites. Sans le module, aucun
  // transfert n'est possible, donc aucun eleve ne peut le porter — et l'afficher
  // ne prouverait rien. Il ne faut pas le renommer « Soldé » tout court : un
  // eleve a jour porte deja « Payé », et deux mots pour la meme chose seraient
  // une confusion de plus.
  const { estActive } = useFeatures();
  const interSitesOuvert = estActive('module.intersites');
  const [activeTab, setActiveTab] = useState('scolarite');
  const [paiements, setPaiements] = useState([]);
  const [transportPaiements, setTransportPaiements] = useState([]);
  const [cantinePaiements, setCantinePaiements] = useState([]);
  const [transportPonctuels, setTransportPonctuels] = useState([]);
  const [cantinePonctuels, setCantinePonctuels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fraisScolaires, setFraisScolaires] = useState({
    scolarite: null,
    transport: null,
    cantine: null
  });
const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
const [historyMoisId, setHistoryMoisId] = useState(null);
const [historyMoisNom, setHistoryMoisNom] = useState(null);
const [historyCategorie, setHistoryCategorie] = useState(null);
const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
// Modifier le tarif et encaisser sont deux gestes de nature differente : l'un
// passe par le Centre de Support, l'autre est immediat. Ils restent separes.
const [isModifierReductionOpen, setIsModifierReductionOpen] = useState(false);
const [isModifierFraisOpen, setIsModifierFraisOpen] = useState(false);
// Remonter ce compteur remonte le journal : une demande qui vient d'etre envoyee
// doit y apparaitre en attente, sans que la gestionnaire ait a rouvrir le dossier.
const [journalVersion, setJournalVersion] = useState(0);

  useEffect(() => {
    if (isOpen && eleve && anneeActive) {
      fetchPaiements();
    }
  }, [isOpen, eleve, anneeActive]);

  // Un eleve rattache a une famille se pilote depuis sa famille : sa reduction
  // peut y suivre une valeur commune, et un accord financier y determine le
  // montant facture.
  const estMembreFamille = Boolean(eleve?.est_en_famille_nombreuse || eleve?.famille_nombreuse_id || eleve?.famille_id);

  // Lien direct : on attend la fin du premier chargement, pour que la fenêtre
  // de correction se pose sur un dossier déjà affiché. La réduction mensuelle
  // d'un membre de famille ne s'ouvre jamais d'ici (pas de bouton pour lui).
  useEffect(() => {
    if (!isOpen || isLoading || !ouvrirAuChargement) return;
    if (ouvrirAuChargement === 'frais') setIsModifierFraisOpen(true);
    if (ouvrirAuChargement === 'reduction' && !estMembreFamille) setIsModifierReductionOpen(true);
    if (onOuvertureConsommee) onOuvertureConsommee();
  }, [isOpen, isLoading, ouvrirAuChargement]);



 const openHistoryModal = (paiement, categorie) => {
  console.log("🔓 Ouverture modal historique:", paiement.mois_nom);
  setHistoryMoisId(paiement.mois_id);
  setHistoryMoisNom(paiement.mois_nom || 'Mois');
  setHistoryCategorie(categorie);
  setIsHistoryModalOpen(true);
};

const closeHistoryModal = () => {
  console.log("🔒 Fermeture modal historique");
  setIsHistoryModalOpen(false);
  setHistoryMoisId(null);
  setHistoryMoisNom(null);
  setHistoryCategorie(null);
};



  const normaliserFraisClasse = (classeResponse) => ({
    scolarite: Number(classeResponse?.frais_mensuel || 0),
    transport: Number(classeResponse?.frais_transport || 0),
    cantine: Number(classeResponse?.frais_cantine || 0)
  });

  const construirePaiementsCategorie = (moisReference = [], paiementsCategorie = [], montantAttendu = 0, categorieId) => {
    const paiementsParMois = new Map(
      (Array.isArray(paiementsCategorie) ? paiementsCategorie : []).map((paiement) => [String(paiement.mois_id), paiement])
    );

    return (Array.isArray(moisReference) ? moisReference : []).map((mois) => {
      const paiementExistant = paiementsParMois.get(String(mois.mois_id));

      if (paiementExistant) {
        return {
          ...paiementExistant,
          categorie: categorieId,
          montant_attendu: Number(paiementExistant.montant_attendu || montantAttendu || 0),
          montant_paye: Number(paiementExistant.montant_paye || 0)
        };
      }

      return {
        ...mois,
        id: `${categorieId}-${mois.mois_id}`,
        categorie: categorieId,
        montant_attendu: Number(montantAttendu || 0),
        montant_paye: 0,
        statut: 'Non payé',
        date_paiement: null,
        date_paiement_complete: null,
        employe_nom: null,
        employe_prenom: null,
        mode_paiement: null,
        numero_transaction: null
      };
    });
  };

  const fetchPaiements = async () => {
    try {
      setIsLoading(true);

      const classeResponse = eleve?.classe_id ? await api.classes.getById(eleve.classe_id) : null;
      const frais = normaliserFraisClasse(classeResponse);
      setFraisScolaires(frais);

      const [scolariteData, transportData, cantineData, transportPonctuelsData, cantinePonctuelsData] = await Promise.all([
        api.finance.getPaiements(eleve.id, anneeActive.id, 'scolarite'),
        frais.transport > 0 ? api.finance.getPaiements(eleve.id, anneeActive.id, 'transport') : Promise.resolve([]),
        frais.cantine > 0 ? api.finance.getPaiements(eleve.id, anneeActive.id, 'cantine') : Promise.resolve([]),
        api.finance.getPaiementsAnnexesPonctuels(eleve.id, anneeActive.id, 'transport'),
        api.finance.getPaiementsAnnexesPonctuels(eleve.id, anneeActive.id, 'cantine')
      ]);

      const scolaritePaiements = Array.isArray(scolariteData) ? scolariteData : [];
      setPaiements(scolaritePaiements);
      setTransportPaiements(frais.transport > 0
        ? construirePaiementsCategorie(scolaritePaiements, transportData, frais.transport, 'transport')
        : []
      );
      setCantinePaiements(frais.cantine > 0
        ? construirePaiementsCategorie(scolaritePaiements, cantineData, frais.cantine, 'cantine')
        : []
      );
      setTransportPonctuels(Array.isArray(transportPonctuelsData?.data) ? transportPonctuelsData.data : (Array.isArray(transportPonctuelsData) ? transportPonctuelsData : []));
      setCantinePonctuels(Array.isArray(cantinePonctuelsData?.data) ? cantinePonctuelsData.data : (Array.isArray(cantinePonctuelsData) ? cantinePonctuelsData : []));
    } catch (error) {
      console.error("Erreur lors de la récupération des paiements:", error);
      setPaiements([]);
      setTransportPaiements([]);
      setCantinePaiements([]);
      setTransportPonctuels([]);
      setCantinePonctuels([]);
      setFraisScolaires({ scolarite: 0, transport: 0, cantine: 0 });
    } finally {
      setIsLoading(false);
    }
  };




// ============================================================================
// FONCTION : Télécharger le reçu de paiement individuel
// ============================================================================
const handleTelechargerRecu = async (paiement, categorieId) => {
  try {
    setToast({ visible: true, message: 'Génération du reçu en cours...', type: 'loading' });
    
    console.log('📄 Téléchargement reçu pour:', paiement.mois_nom, categorieId);
    
    // Appeler l'API pour générer le PDF
    const pdfBlob = await api.finance.genererRecuIndividuel(
      eleve.id,
      paiement.mois_id,
      anneeActive.id,
      categorieId
    );
    
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('Le fichier reçu est vide');
    }
    
    const filename = `Recu-${eleve.nom}-${eleve.prenom}-${paiement.mois_nom}-${categorieId}.pdf`;
    
    // Créer un blob explicitement en PDF
    const pdfBlobFixed = new Blob([pdfBlob], { type: 'application/pdf' });
    
    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(pdfBlobFixed);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Nettoyer après un délai
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    setToast({ 
      visible: true, 
      message: `Reçu pour ${paiement.mois_nom} téléchargé avec succès !`, 
      type: 'success' 
    });
    
  } catch (error) {
    console.error('❌ Erreur téléchargement reçu:', error);
    setToast({ 
      visible: true, 
      message: `❌ Erreur: ${error.message}`, 
      type: 'error' 
    });
  }
};


  
  // 🆕 Fonction pour vérifier si une catégorie est disponible

const getPaiementsPonctuelsCategorie = (categorie) => {
  if (categorie === 'transport') return transportPonctuels;
  if (categorie === 'cantine') return cantinePonctuels;
  return [];
};

const handleTelechargerRecuAnnexePonctuel = async (paiement) => {
  try {
    setToast({ visible: true, message: 'Generation du recu ponctuel en cours...', type: 'loading' });

    const pdfBlob = await api.finance.genererRecuAnnexePonctuel(paiement.paiement_id || paiement.id);
    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('Le fichier recu est vide');
    }

    const pdfBlobFixed = new Blob([pdfBlob], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(pdfBlobFixed);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Recu-annexe-${paiement.categorie || 'annexe'}-${paiement.date_service || paiement.id}.pdf`;
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    setToast({ visible: true, message: 'Recu ponctuel telecharge avec succes.', type: 'success' });
  } catch (error) {
    setToast({ visible: true, message: error.message || 'Erreur lors du telechargement du recu ponctuel', type: 'error' });
  }
};

const renderPaiementsPonctuels = (categorie) => {
  if (!['transport', 'cantine'].includes(categorie)) return null;

  const paiementsPonctuels = getPaiementsPonctuelsCategorie(categorie);
  const label = categorie === 'transport' ? 'Transport ponctuel' : 'Cantine ponctuelle';

  return (
    <div className="rounded-md border border-[#246990]/20 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 bg-[#246990]/5 px-4 py-3 border-b border-[#246990]/10">
        <div>
          <h3 className="font-semibold text-[#246990]">{label}</h3>
          <p className="text-xs text-gray-600">Tickets encaisses a la journee, separes des abonnements mensuels.</p>
        </div>
        <span className="rounded-full border border-[#246990]/20 bg-white px-3 py-1 text-xs font-semibold text-[#246990]">
          {paiementsPonctuels.length} ticket(s)
        </span>
      </div>

      {paiementsPonctuels.length === 0 ? (
        <div className="p-5 text-sm text-gray-500">
          Aucun paiement ponctuel enregistre pour cette categorie.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Date du service</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Type</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600">Montant</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Enregistre par</th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">Date paiement</th>
                <th className="px-4 py-2 text-center font-medium text-gray-600">Recu</th>
              </tr>
            </thead>
            <tbody>
              {paiementsPonctuels.map((paiement) => (
                <tr key={`${categorie}-ponctuel-${paiement.id}`} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium text-gray-800">{paiement.date_service_formatee || paiement.date_service}</td>
                  <td className="px-4 py-3 text-gray-700">{paiement.type_label || '-'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#246990]">{formatMontant(paiement.montant)}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {paiement.employe_prenom || paiement.employe_nom
                      ? `${paiement.employe_prenom || ''} ${paiement.employe_nom || ''}`.trim()
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{paiement.date_paiement_formatee || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => handleTelechargerRecuAnnexePonctuel(paiement)}
                      className="inline-flex items-center px-3 py-1.5 bg-[#246990] text-white text-xs font-medium rounded-lg hover:bg-[#1f5c7d] transition-colors"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Telecharger
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
  const isCategorieDisponible = (categorieId) => {
    if (categorieId === 'scolarite') return true;
    const montant = fraisScolaires[categorieId];
    return montant && montant > 0;
  };
  
  const calculerTotaux = (paiementsData = paiements) => {
    if (!paiementsData.length) return { total: 0, paye: 0, restant: 0 };
    
    const total = paiementsData.reduce((sum, p) => sum + Number(p.montant_attendu || 0), 0);
    const paye = paiementsData.reduce((sum, p) => sum + Number(p.montant_paye || 0), 0);
    
    return {
      total,
      paye,
      restant: total - paye
    };
  };
  
  const totaux = calculerTotaux();
  const totauxTransport = calculerTotaux(transportPaiements);
  const totauxCantine = calculerTotaux(cantinePaiements);
  
  const handleGenerateReceipt = async (paiementId) => {
    try {
      const response = await api.finance.generateReceipt({
        eleve_id: eleve.id,
        paiement_id: paiementId,
        annee_scolaire_id: anneeActive.id
      });
      
      if (response.success && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.filename || 'recu-paiement.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Erreur lors de la génération du reçu");
      }
    } catch (error) {
      console.error("Erreur lors de la génération du reçu:", error);
    }
  };

  const organiserParTrimestre = (paiementsData) => {
    const trimestres = {};
    const moisTraites = new Set();
    
    const paiementsTries = [...paiementsData].sort((a, b) => {
      if ((a.montant_paye > 0 && b.montant_paye === 0) || 
          (a.montant_paye > 0 && a.montant_paye >= a.montant_attendu && b.montant_paye < b.montant_attendu)) {
        return -1;
      }
      if ((b.montant_paye > 0 && a.montant_paye === 0) || 
          (b.montant_paye > 0 && b.montant_paye >= b.montant_attendu && a.montant_paye < a.montant_attendu)) {
        return 1;
      }
      return a.mois_id - b.mois_id;
    });
    
    paiementsTries.forEach(paiement => {
      const moisKey = `${paiement.mois_id}`;
      
      if (moisTraites.has(moisKey)) {
        return;
      }
      
      moisTraites.add(moisKey);
      
      const trimestre = paiement.trimestre || 
                      (paiement.mois_id >= 1 && paiement.mois_id <= 3 ? 1 :
                       paiement.mois_id >= 4 && paiement.mois_id <= 6 ? 2 : 3);
      
      if (!trimestres[trimestre]) {
        trimestres[trimestre] = [];
      }
      
      trimestres[trimestre].push(paiement);
    });
    
    return trimestres;
  };
  


  const renderPaiementsTable = (paiementsData, categorieId) => {
  const trimestres = organiserParTrimestre(paiementsData);
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-2 text-left font-medium">Mois</th>
            <th className="px-4 py-2 text-right font-medium">Montant dû</th>
            <th className="px-4 py-2 text-right font-medium">Payé</th>
            <th className="px-4 py-2 text-right font-medium">Reste</th>
            <th className="px-4 py-2 text-center font-medium">Statut</th>
            <th className="px-4 py-2 text-left font-medium">Date paiement</th>
            <th className="px-4 py-2 text-left font-medium">Enregistré par</th>
            <th className="px-4 py-2 text-center font-medium">Reçu</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="8" className="px-4 py-8">
                <LoadingSpinner message="Chargement des données..." size="sm" />
              </td>
            </tr>
          ) : paiementsData.length === 0 ? (
            <tr>
              <td colSpan="8" className="px-4 py-8 text-center">Aucun paiement enregistré</td>
            </tr>
          ) : (
            Object.keys(trimestres).map((trimestre) => (
              <React.Fragment key={`trimestre-${trimestre}`}>
                <tr className="bg-blue-100">
                  <td colSpan="8" className="px-4 py-2 font-semibold">
                    {trimestre === '1' ? 'Premier trimestre' : 
                     trimestre === '2' ? 'Deuxième trimestre' : 
                     trimestre === '3' ? 'Troisième trimestre' : `Trimestre ${trimestre}`}
                  </td>
                </tr>
                

               {trimestres[trimestre].map(paiement => {
                const montantDu = Number(paiement.montant_attendu || 0);
                const montantPaye = Number(paiement.montant_paye || 0);
                const montantRestant = montantDu - montantPaye;
                
                // 🆕 Utiliser le statut de la base de données si présent
                let statut = paiement.statut || 'Non payé';
                
                // Si pas de statut explicite, calculer selon les montants
                if (!paiement.statut) {
                  if (montantRestant <= 0) {
                    statut = 'Payé';
                  } else if (montantPaye > 0) {
                    statut = 'Partiel';
                  } else {
                    statut = 'Non payé';
                  }
                }


                  
                  return (
                    <tr key={`${categorieId}-${paiement.id || paiement.mois_id}`} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">{paiement.mois_nom || "Mois non spécifié"}</td>
                      <td className="px-4 py-3 text-right">{formatMontant(montantDu)}</td>
                      <td className="px-4 py-3 text-right">{formatMontant(montantPaye)}</td>
                      <td className="px-4 py-3 text-right">{formatMontant(montantRestant)}</td>

                      <td className="px-4 py-3 text-center">
                        {paiement.statut_frequentation === 'non_scolarise' ? (
                          <span className="bg-gray-100 text-gray-600 py-1 px-3 rounded-full text-xs font-medium flex items-center justify-center gap-1 mx-auto w-fit">
                            <XCircle size={12} />
                            Non scolarisé
                          </span>
                        ) : statut === 'Payé' ? (
                          <span className="bg-green-100 text-green-800 py-1 px-3 rounded-full text-xs font-medium flex items-center justify-center gap-1 mx-auto w-fit">
                            <CheckCircle size={12} />
                            Payé
                          </span>
                        ) : statut === 'Payé au prorata' ? (
                          <span className="bg-blue-100 text-blue-800 py-1 px-3 rounded-full text-xs font-medium flex items-center justify-center gap-1 mx-auto w-fit">
                            <Calculator size={12} />
                            Payé au prorata
                          </span>
                          ) : (interSitesOuvert && statut === 'Soldé') ? (
                          <div className="relative group">
                            <span className="bg-teal-100 text-teal-800 py-1 px-3 rounded-full text-xs font-medium flex items-center justify-center gap-1 mx-auto w-fit cursor-help">
                              <Network size={12} />
                              Soldé (site source)
                            </span>
                            <div className="absolute right-1/2 translate-x-1/2 bottom-full mb-2 w-72 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-teal-200">
                              <div className="flex items-start gap-2">
                                <Network className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="font-semibold text-teal-700 mb-1">Payé sur le site d'origine</p>
                                  <p className="text-gray-600">
                                    Ce mois a été réglé sur l'ancien établissement de l'élève avant son transfert inter-sites.
                                  </p>
                                </div>
                              </div>
                              <div className="absolute -bottom-2 right-1/2 translate-x-1/2 w-4 h-4 bg-white border-r border-b border-teal-200 transform rotate-45"></div>
                            </div>
                          </div>
                        ) : statut === 'Partiel' ? (
                          <span className="bg-yellow-100 text-yellow-800 py-1 px-3 rounded-full text-xs font-medium flex items-center justify-center gap-1 mx-auto w-fit">
                            <AlertCircle size={12} />
                            Partiel
                          </span>
                        ) : (
                          <span className="bg-red-100 text-red-800 py-1 px-3 rounded-full text-xs font-medium flex items-center justify-center gap-1 mx-auto w-fit">
                            <XCircle size={12} />
                            Non payé
                          </span>
                        )}
                      </td>


                    <td className="px-4 py-3">
                    {paiement.date_paiement ? (
                      <div className="space-y-0.5">
                        <div className="text-gray-700 font-medium">
                          {new Date(paiement.date_paiement).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </div>
                        {paiement.date_paiement_complete && (
                          <div className="text-gray-500">
                            {new Date(paiement.date_paiement_complete).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>

                  

                   <td className="px-4 py-3">
                      {paiement.employe_nom && paiement.employe_prenom ? (
                        <div className="flex items-center space-x-2 relative group">
                          <User className="w-4 h-4 text-[#246990]" />
                          <span className="text-gray-700">
                            {paiement.employe_prenom} {paiement.employe_nom}
                          </span>
                          
                          {/* Tooltip retenue locale */}
                          {paiement.mode_paiement === 'Retenue sur salaire' && 
                          paiement.parent_employe_nom && 
                          paiement.parent_employe_prenom && (
                            <div className="absolute right-0 bottom-full mb-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-orange-200">
                              <div className="flex items-start gap-2">
                                <User className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="font-semibold text-orange-700 mb-1">Enfant à charge</p>
                                  <p className="text-gray-600">
                                    Ce paiement a été effectué via <span className="font-semibold">retenue sur salaire</span> pour l'enfant de{' '}
                                    <span className="font-semibold text-orange-600">
                                      {paiement.parent_employe_prenom} {paiement.parent_employe_nom}
                                    </span>.
                                  </p>
                                </div>
                              </div>
                              {/* Petite flèche */}
                              <div className="absolute -bottom-2 right-12 w-4 h-4 bg-white border-r border-b border-orange-200 transform rotate-45"></div>
                            </div>
                          )}
                        </div>
                      ) : paiement.mode_paiement && paiement.mode_paiement.includes('inter-sites') ? (
                        /* 🌐 Paiement inter-sites */
                        <div className="relative group">
                          <div className="flex items-center gap-1.5 cursor-help">
                            <Network size={16} className="text-gray-600" />
                            <span className="text-teal-700 text-xs font-medium">
                              Liaison inter-sites
                            </span>
                          </div>
                          {/* Tooltip inter-sites */}
                          <div className="absolute right-0 bottom-full mb-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-teal-200">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-semibold text-teal-700 mb-1">Paiement inter-sites</p>
                                <p className="text-gray-600">
                                  Ce paiement a été effectué automatiquement via{' '}
                                  <span className="font-semibold">retenue sur salaire</span> depuis un autre site de l'établissement.
                                </p>
                                <p className="text-gray-500 mt-2 text-xs bg-teal-50 p-2 rounded border border-teal-100">
                                  <span className="font-semibold">Initié par :</span><br/>
                                  {(() => {
                                    const match = paiement.mode_paiement.match(/inter-sites:\s*(.+?)\)/);
                                    if (match) {
                                      const parts = match[1].split(' - ');
                                      return (
                                        <>
                                          Employé : <span className="font-bold text-teal-700">{parts[0]?.trim()}</span><br/>
                                          Site : <span className="font-bold text-teal-700">{parts[1]?.trim()?.toUpperCase()}</span>
                                        </>
                                      );
                                    }
                                    return paiement.mode_paiement;
                                  })()}
                                </p>
                                <p className="text-gray-500 mt-2 italic">
                                  Consultez le module <span className="font-semibold text-teal-600">Liaison Inter-Sites</span> pour le détail complet.
                                </p>
                              </div>
                            </div>
                            {/* Petite flèche */}
                            <div className="absolute -bottom-2 right-12 w-4 h-4 bg-white border-r border-b border-teal-200 transform rotate-45"></div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>


                     <td className="px-4 py-3 text-center">
                    {(statut === 'Payé' || statut === 'Payé au prorata' || statut === 'Partiel') ? (
                    <button
                      onClick={() => handleTelechargerRecu(paiement, categorieId)}
                      className="inline-flex items-center px-3 py-1.5 bg-[#246990] text-white text-xs font-medium rounded-lg hover:bg-[#1f5c7d] transition-colors"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Télécharger
                    </button>

                    ) : (
                      <span className="text-gray-400 text-xs italic">-</span>
                    )}
                  </td>


                    </tr>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
  


  const renderFinancialSummary = (totauxData, categorie) => {
    let bgColor;
    let textColor;
    
    if (categorie === 'scolarite') {
      bgColor = 'bg-[#246990]/10';
      textColor = 'text-[#246990]';
    } else if (categorie === 'transport') {
      bgColor = 'bg-[#c66d59]/10';
      textColor = 'text-[#c66d59]';
    } else if (categorie === 'cantine') {
      bgColor = 'bg-[#319190]/10';
      textColor = 'text-[#319190]';
    } else {
      bgColor = 'bg-gray-700/10';
      textColor = 'text-gray-700';
    }
    
    return (
      <div className={`${bgColor} border border-${textColor}/20 p-4 rounded-md mb-6`}>
        <h3 className={`${textColor} mb-2`}>
          Résumé financier
          {categorie !== 'scolarite' && (
            <span className="ml-2 font-normal opacity-80">
              (Frais mensuels: {formatMontant(fraisScolaires[categorie] || 0)})
            </span>
          )}
        </h3>
        <div className="flex flex-wrap gap-4">
          <div>
            <span className={`${textColor}`}>Total annuel:</span> <span>{formatMontant(totauxData.total)}</span>
          </div>
          <div>
            <span className={`${textColor}`}>Payé:</span> <span>{formatMontant(totauxData.paye)}</span>
          </div>
          <div>
            <span className={`${textColor}`}>Restant:</span> <span>{formatMontant(totauxData.restant)}</span>
          </div>
        </div>
      </div>
    );
  };

  // 🆕 Rendu du contenu avec message si catégorie non disponible
  const renderCategorieContent = (categorie, paiementsData, totauxData) => {
    if (!isCategorieDisponible(categorie)) {
      return (
        <div className="space-y-4">
          {renderPaiementsPonctuels(categorie)}
          <div className="p-8 text-center bg-gray-50 rounded-md">
            <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-2">
              Aucun abonnement mensuel de {categorie === 'transport' ? 'transport' : 'cantine'} pour cette classe
            </p>
            <p className="text-sm text-gray-500">
              La classe <strong>{eleve?.classe_nom}</strong> n'a pas de frais mensuel de {categorie === 'transport' ? 'transport' : 'cantine'} configuré. Les tickets ponctuels restent visibles ici.
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        {renderFinancialSummary(totauxData, categorie)}
        {renderPaiementsTable(paiementsData, categorie)}
        {renderPaiementsPonctuels(categorie)}
      </div>
    );
  };
  if (!eleve) return null;
  
  return (
     <>
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open && isOpen) {
          return;
        }
        onClose();
      }}
    >
      <DialogContent 
          className="w-[95vw] max-w-7xl !max-h-[95vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <DialogTitle className="text-xl font-bold text-[#246990]">
            Dossier financier - {eleve.prenom} {eleve.nom} {eleve.matricule && `(${eleve.matricule})`}
          </DialogTitle>
          <div className="flex items-center gap-2">
            {/* Un membre de famille se pilote depuis sa famille (voir
                `estMembreFamille`). */}
            {!estMembreFamille && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsModifierReductionOpen(true)}
                className="gap-1.5 border-[#246990]/30 text-[#246990] hover:bg-[#eef7fb]"
              >
                <Calculator className="w-4 h-4" />
                Frais mensuels
              </Button>
            )}
            {/* Les frais de début d'année existent pour tous, membres de famille
                compris : ils vivent sur l'élève, pas sur la famille. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsModifierFraisOpen(true)}
              className="gap-1.5 border-[#246990]/30 text-[#246990] hover:bg-[#eef7fb]"
            >
              <FileText className="w-4 h-4" />
              Frais de début d'année
            </Button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </DialogHeader>
        
        <div className="flex-grow overflow-y-auto p-4 pt-0 space-y-4">
          <div className="bg-blue-50/50 p-4 rounded-md mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-2">
                <User size={20} className="text-[#246990]" />
                <div>
                  <div className="text-sm text-[#246990]">Élève</div>
                  <div className="font-medium text-gray-800">{eleve.prenom} {eleve.nom}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <FileText size={20} className="text-[#246990]" />
                <div>
                  <div className="text-sm text-[#246990]">Classe</div>
                  <div className="font-medium text-gray-800">{eleve.classe_nom}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <User size={20} className="text-[#246990]" />
                <div>
                  <div className="text-sm text-[#246990]">Parent</div>
                  <div className="font-medium text-gray-800">
                    {eleve.parent_nom || "Non renseigné"}
                    {eleve.parent_telephone && (
                      <span className="text-sm text-gray-500 ml-2">
                        (Tél: {eleve.parent_telephone})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Placé sous l'identité et hors des onglets : la question « pourquoi ce
              montant ? » se pose quelle que soit la catégorie consultée. Replié par
              défaut, il ne coûte rien à ceux qui ne la posent pas. */}
          <JournalModificationsEleve key={journalVersion} eleveId={eleve.id} />

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full bg-gray-100 p-1 rounded-md">
              <TabsTrigger 
                value="scolarite"
                className="flex-1 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
              >
                <div className="flex items-center justify-center gap-1.5">
                  <GraduationCap size={16} />
                  <span>Scolarité</span>
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="transport"
                className={`flex-1 data-[state=active]:bg-[#c66d59] data-[state=active]:text-white ${
                  !isCategorieDisponible('transport') ? 'opacity-70 cursor-pointer' : ''
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <Bus size={16} />
                  <span>Transport</span>
                  {!isCategorieDisponible('transport') && (
                    <span className="ml-1 text-xs">(Aucun frais)</span>
                  )}
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="cantine"
                className={`flex-1 data-[state=active]:bg-[#319190] data-[state=active]:text-white ${
                  !isCategorieDisponible('cantine') ? 'opacity-70 cursor-pointer' : ''
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <Utensils size={16} />
                  <span>Cantine</span>
                  {!isCategorieDisponible('cantine') && (
                    <span className="ml-1 text-xs">(Aucun frais)</span>
                  )}
                </div>
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="scolarite" className="mt-0">
              {renderCategorieContent('scolarite', paiements, totaux)}
            </TabsContent>
            
            <TabsContent value="transport" className="mt-0">
              {renderCategorieContent('transport', transportPaiements, totauxTransport)}
            </TabsContent>
            
            <TabsContent value="cantine" className="mt-0">
              {renderCategorieContent('cantine', cantinePaiements, totauxCantine)}
            </TabsContent>
          </Tabs>
        </div>
        
      <DialogFooter className="p-4 pt-2 border-t">
      <div className="flex justify-end w-full">
        <Button 
  variant="outline"
  className="text-sm px-4 py-2"
  onClick={onClose}
>
  Fermer
</Button>

      </div>
    </DialogFooter>



      </DialogContent>
    </Dialog>
    {/* 🆕 MODAL HISTORIQUE SORTI DU DIALOG */}
      <PaymentHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={closeHistoryModal}
        eleve={eleve}
        moisId={historyMoisId}
        moisNom={historyMoisNom}
        anneeActive={anneeActive}
        formatMontant={formatMontant}
        categorie={historyCategorie}
      />

      {/* Sorti du Dialog comme l'historique : imbriquer un overlay dans le
          DialogContent de Radix le laisse sous la couche du dialogue parent. */}
      {isModifierReductionOpen && (
        <ModalModifierReductionEleve
          eleve={eleve}
          anneeScolaireId={anneeActive?.id || anneeActive}
          onClose={() => setIsModifierReductionOpen(false)}
          onSuccess={(result) => {
            setIsModifierReductionOpen(false);
            setJournalVersion((version) => version + 1);
            setToast({
              visible: true,
              message: result?.message || 'Demande envoyée au Centre de Support.',
              type: 'success'
            });
          }}
        />
      )}

      {isModifierFraisOpen && (
        <ModalModifierFraisDebutAnnee
          eleve={eleve}
          anneeScolaireId={anneeActive?.id || anneeActive}
          onClose={() => setIsModifierFraisOpen(false)}
          onSuccess={(result) => {
            setIsModifierFraisOpen(false);
            setJournalVersion((version) => version + 1);
            setToast({
              visible: true,
              message: result?.message || 'Demande envoyée au Centre de Support.',
              type: 'success'
            });
          }}
        />
      )}

      {/* 🆕 TOAST */}
      {toast.visible && (
        <ToastPortal>
          {shouldUseLongToast(toast.message, toast.type, toast) ? (
            <LongToast
              show
              title={toast.title}
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ ...toast, visible: false })}
            />
          ) : (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast({ ...toast, visible: false })}
            />
          )}
        </ToastPortal>
      )}

    </>
  );
};
    
  
export default ComptaPaymentDetailsModal;